import React, { FC, useCallback } from 'react';
import DropzoneField from 'components/DropzoneField';
import { t } from 'i18next';
import { Field, useField } from 'react-final-form';
import validations from 'validations';
import { DOCUMENT_FILES, IM_MAX_SIZE } from 'data/global';
import { DropzoneUpload } from 'models/Dropzone';
import { ListingType } from 'models/Listing';
import {
  LISTING_CREATION_METHOD,
  ListingDocumentUploadedEventProps,
} from 'models/MixpanelEvents';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getFileExtension, getListingTypeLabel } from 'utils';
import useUploadIm, { LISTING_IM } from 'hooks/useUploadIm';
import { LISTING_TYPE_FLOW } from 'hooks/useDraftListingOptions';

type ImFieldProps = { dealRoomId: string; dealName?: string };

const ImField: FC<ImFieldProps> = ({ dealRoomId, dealName }) => {
  const { uploadListingIm, deleteListingIm } = useUploadIm(dealRoomId);

  // The type the AI will draft against, picked right above this field.
  const {
    input: { value: listingType },
  } = useField<ListingType>(LISTING_TYPE_FLOW, {
    subscription: { value: true },
  });

  const getMixpanelProps = useGetMixpanelProps();

  // Reported on selection, before the request that hands the document to BE - so a
  // document that never finishes uploading still counts as an upload attempt.
  const uploadIm = useCallback<DropzoneUpload>(
    (file, ...rest) => {
      mixpanelService.listingDocumentUploaded(
        getMixpanelProps({
          dealId: dealRoomId,
          dealName,
          listingType: t(getListingTypeLabel(listingType)),
          creationMethod: LISTING_CREATION_METHOD.AI,
          fileFormat: getFileExtension(file?.name),
          fileSizeInBytes: file?.size,
        }) as ListingDocumentUploadedEventProps,
      );

      return uploadListingIm(file, ...rest);
    },
    [dealName, dealRoomId, getMixpanelProps, listingType, uploadListingIm],
  );

  return (
    <Field
      accept={DOCUMENT_FILES}
      component={DropzoneField}
      maxFiles={1}
      maxSize={IM_MAX_SIZE}
      showApplicationAsIframe
      dropMessage={t('dragAndDropPDForWord')}
      name={LISTING_IM}
      uploadRequest={uploadIm}
      deleteRequest={deleteListingIm}
      validate={validations.required()}
    />
  );
};

export default ImField;
